import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Typography } from '@mui/material';
import { SHIFT_TYPES } from '../../../../utils/constants/shiftConstants';
import ShiftTimePicker from './components/ShiftTimePicker';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';
import { shouldOpenPicker, getShiftSubtitle } from '../../../../utils/helpers/shifts/shiftsHelpers';
import { TimeRow, TimeInfo, StatusText } from './ShiftTimeEntry.styles';

dayjs.extend(localizedFormat);

/**
 * Displays information about a single shift time entry (start or end time), including:
 * - Title and scheduled time
 * - Status or error messages
 * - Time display/input:
 *   1. For past shifts (shift.start <= now):
 *      Shows read-only time (actual clock time if logged, scheduled time in red if missed)
 *   2. For future shifts (shift.start > now):
 *      Shows editable time picker
 */
const ShiftTimeEntry = ({ shift, openTimePicker, setOpenPicker, onTimeChange, error, disabled, isStartTime }) => {
  // Use venue timezone formatters
  const { formatTime } = useTimeFormatters();

  // Derived values
  const scheduledTime = isStartTime ? shift.start : shift.end;
  const actualTime = isStartTime ? shift.clockedInAt : shift.clockedOutAt;
  const hasScheduledTimePassed = scheduledTime && scheduledTime < new Date();
  const subtitleData = getShiftSubtitle(isStartTime, shift, formatTime);
  // Determine whether the picker should be open
  const isPickerOpen = shouldOpenPicker(isStartTime, shift, openTimePicker, setOpenPicker, SHIFT_TYPES);

  // Determine status (used for styling the time text)
  const pastShiftStatus = actualTime ? 'success' : hasScheduledTimePassed ? 'error' : 'info';

  return (
    <TimeRow className={!isStartTime ? 'last-element' : ''}>
      <TimeInfo>
        <Typography variant="bodyRegular">{isStartTime ? 'Shift start' : 'Shift end'}</Typography>

        {subtitleData && (
          <StatusText status={subtitleData.status} variant="subheadlineRegular">
            {subtitleData.shiftSubtitle}
          </StatusText>
        )}
        {error && (
          <StatusText status="error" variant="footNoteRegular">
            {error}
          </StatusText>
        )}
      </TimeInfo>
      {!hasScheduledTimePassed && !actualTime ? (
        <ShiftTimePicker
          scheduledTime={scheduledTime ? new Date(scheduledTime) : null}
          isPickerOpen={isPickerOpen}
          status={subtitleData?.status || 'info'}
          disabled={disabled}
          onTimeChange={onTimeChange}
          setOpenPicker={setOpenPicker}
        />
      ) : (
        <StatusText status={pastShiftStatus} variant="subheadlineRegular">
          {actualTime ? formatTime(actualTime) : 'Missed'}
        </StatusText>
      )}
    </TimeRow>
  );
};

ShiftTimeEntry.propTypes = {
  shift: PropTypes.object,
  openTimePicker: PropTypes.bool.isRequired,
  setOpenPicker: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  isStartTime: PropTypes.bool.isRequired,
};

export default ShiftTimeEntry;
